import React, { useState, useEffect } from 'react'
import ProposalReviewForm from '../../../forms/ProposalReviewForm'
import LeaseConditions from './LeaseConditions'
import { Button } from '../../../ui/Button'
import {
  ProposalQuoteAdjustments,
  ProposalQuoteConditions,
} from '@/types/management.types'

interface LeaseAdjustmentsProps {
  hideButtons?: boolean
  adjustments?: ProposalQuoteAdjustments
  conditions?: ProposalQuoteConditions
  onAdjustmentsChange?: (adjustments: ProposalQuoteAdjustments) => void
  onConfirm?: (adjustments: ProposalQuoteAdjustments) => void
  onCancel?: () => void
}

const LeaseAdjustments: React.FC<LeaseAdjustmentsProps> = ({
  hideButtons = false,
  adjustments,
  conditions,
  onAdjustmentsChange,
  onConfirm,
  onCancel,
}) => {
  const [formData, setFormData] = useState<ProposalQuoteAdjustments>(
    adjustments || {
      leaseTerm: 24,
      advancePayment: 20,
      residualValue: 45,
      guaranteeDeposit: 2,
    }
  )

  const [calculatedConditions, setCalculatedConditions] = useState<
    ProposalQuoteConditions | undefined
  >(conditions)

  // Update form data when adjustments prop changes
  useEffect(() => {
    if (adjustments) {
      setFormData(adjustments)
    }
  }, [adjustments])

  // Mock calculation function - replace with real calculation logic
  const calculateConditions = (
    adjustments: ProposalQuoteAdjustments
  ): ProposalQuoteConditions => {
    // This is a simplified calculation - replace with your actual business logic
    const baseAmount = 400000
    const leaseAmount = (
      (baseAmount * (100 - adjustments.advancePayment)) /
      100
    ).toString()
    const residualValueAmount = (
      (baseAmount * adjustments.residualValue) /
      100
    ).toString()
    const monthlyRent = (
      parseFloat(leaseAmount) / adjustments.leaseTerm
    ).toString()

    return {
      leaseAmount,
      insurance: '8500',
      totalInitialAmount: (
        (baseAmount * adjustments.advancePayment) /
        100
      ).toString(),
      openingFee: '3000',
      residualValueNoTax: residualValueAmount,
      monthlyRentNoTax: monthlyRent,
      monthlyDiscount: '500',
      totalRentWithDiscount: (parseFloat(monthlyRent) - 500).toString(),
      rentTax: (parseFloat(monthlyRent) * 0.16).toString(),
      reserveFund: '750',
      totalRentWithTax: (parseFloat(monthlyRent) * 1.16).toString(),
    }
  }

  const handleFormChange = (values: ProposalQuoteAdjustments) => {
    setFormData(values)

    // Calculate new conditions based on form values
    const newConditions = calculateConditions(values)
    setCalculatedConditions(newConditions)

    // Notify parent component of changes
    if (onAdjustmentsChange) {
      onAdjustmentsChange(values)
    }
  }

  const handleFormSubmit = (values: ProposalQuoteAdjustments) => {
    setFormData(values)
    const newConditions = calculateConditions(values)
    setCalculatedConditions(newConditions)
  }

  const handleCancel = () => {
    if (onCancel) {
      onCancel()
    }
  }

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm(formData)
    }
  }

  return (
    <div className="w-full">
      {/* Header Section */}
      <div className="bg-[#E4EFFF] px-6 py-2">
        <h2 className="text-lg font-semibold text-center text-gray-800 mb-2">
          Ajusta tu cotización a tus necesidades
        </h2>
      </div>
      <div className="bg-transparent rounded-lg p-4 mb-6">
        <p className="text-sm text-gray-900 text-center">
          A medida que modifiques estos campos, verás reflejados los cambios en
          las condiciones finales del arrendamiento. Una vez que confirmes, esta
          será la propuesta aceptada y continuaremos con el proceso:
        </p>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Column - Form */}
        <div className="bg-transparent rounded-lg p">
          <ProposalReviewForm
            initialValues={formData}
            onSubmit={handleFormSubmit}
            onChange={handleFormChange}
          />
        </div>

        {/* Right Column - Lease Conditions */}
        <div className="bg-transparent rounded-lg p">
          <LeaseConditions conditions={calculatedConditions || conditions} />
        </div>
      </div>

      {/* Action Buttons */}
      {!hideButtons && (
        <div className="flex justify-center gap-4 mt-8">
          <Button
            variant="outline"
            size="sm"
            onClick={handleCancel}
            className="px-8 py-3 min-w-[260px]"
          >
            No es buena opción para mi
          </Button>

          <Button
            variant="primary"
            size="sm"
            onClick={handleConfirm}
            className="px-8 py-3 min-w-[260px]"
          >
            Confirmar y continuar
          </Button>
        </div>
      )}
    </div>
  )
}

export default LeaseAdjustments
